import { Dialog, DialogContent } from "@/components/ui/dialog";
import { MapPin, X, Thermometer, Coins, FileCheck2, Sparkles, Loader2, ChevronDown, Compass } from "lucide-react";
import { useEffect, useState } from "react";
import { getSpotlight, hasCuratedSpotlight, fetchSpotlightRemote } from "@/lib/destinations";

// Full-screen "Destination Spotlight" — opened when a user taps a Popular chip,
// Journey-of-the-Day banner, or the hero photo. Rich info card with weather / currency /
// visa + 3 must-see attractions, and a glowing CTA that hands off to the AI chat.
//
// For ANY destination not in the static SPOTLIGHTS dict, it shows the instant generic
// card first, then asynchronously fetches an AI-generated card from /api/spotlight/{dest}
// and swaps in the richer content once loaded.
export default function DestinationSpotlight({ open, onOpenChange, destination, onPlan }) {
  const [data, setData] = useState(() => getSpotlight(destination));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !destination) return;
    const instant = getSpotlight(destination);
    setData(instant);
    // If we already have a curated (non-placeholder) card, no network call needed.
    if (hasCuratedSpotlight(destination)) return;
    let cancelled = false;
    setLoading(true);
    fetchSpotlightRemote(destination)
      .then((remote) => {
        if (cancelled || !remote) return;
        setData(remote);
      })
      .catch(() => {
        /* keep instant fallback on failure */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, destination]);

  if (!data) return null;

  const handlePlan = () => {
    onOpenChange(false);
    if (onPlan) onPlan(data.name);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[96vw] md:max-w-[960px] lg:max-w-[1100px] p-0 overflow-hidden border-0 bg-white shadow-[0_30px_80px_rgba(0,0,0,0.35)] rounded-2xl"
        data-testid="spotlight-modal"
      >
        <div className="max-h-[92vh] overflow-y-auto">
          {/* Hero */}
          <div className="relative h-[44vh] md:h-[52vh] overflow-hidden">
            <img
              src={data.hero}
              alt={data.name}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="spotlight-hero"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/70" />
            <button
              onClick={() => onOpenChange(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur"
              aria-label="Close"
              data-testid="spotlight-close"
            >
              <X className="w-5 h-5" />
            </button>
            {loading && (
              <div
                className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/55 text-white/95 backdrop-blur text-[11px] tracking-wide"
                data-testid="spotlight-loading"
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Crafting your {data.name} card…
              </div>
            )}
            <div className="absolute bottom-6 left-6 md:left-10 right-6 text-white animate-fade-up">
              <div className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-white/85 mb-1.5">
                <MapPin className="w-3.5 h-3.5" />
                Destination Spotlight
              </div>
              <h1
                className="font-serif-vp text-4xl md:text-6xl leading-[1.02] drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)]"
                data-testid="spotlight-title"
              >
                {data.name}
              </h1>
              {data.tagline && (
                <div className="mt-2 text-white/90 text-base md:text-lg font-light italic drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                  {data.tagline}
                </div>
              )}
              {data.priceFrom && (
                <div className="mt-3 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[var(--vp-accent)]/95 text-[var(--vp-primary)] text-xs md:text-sm font-bold tracking-wide shadow-[0_6px_20px_rgba(0,0,0,0.35)]">
                  {data.priceFrom}
                </div>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="px-6 md:px-10 py-8 space-y-9 animate-fade-up">
            {/* Quick facts */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="spotlight-facts">
              <FactCard
                icon={<Thermometer className="w-4 h-4" />}
                label="Weather & Season"
                primary={data.weather}
                secondary={`Peak: ${data.bestTime} · Visiting off-peak? Our concierge can pivot to seasonal indoor gems.`}
              />
              <FactCard
                icon={<Coins className="w-4 h-4" />}
                label="Currency"
                primary={data.currency}
                secondary={data.inrRate}
              />
              <FactCard
                icon={<FileCheck2 className="w-4 h-4" />}
                label="Visa (Indian Passport)"
                primary={data.visa}
                secondary="We'll handle your paperwork"
              />
            </div>

            {/* Top attractions — text-only with show-more toggle (no images = no mismatch) */}
            <AttractionsList items={data.attractions} destination={data.name} />

            {/* Glowing CTA */}
            <div className="flex flex-col items-center pt-2">
              <button
                type="button"
                onClick={handlePlan}
                className="relative inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[var(--vp-primary)] hover:bg-[#0e1b3d] text-white font-bold tracking-[0.12em] uppercase text-sm transition-transform hover:-translate-y-0.5 shadow-[0_18px_40px_rgba(26,115,232,0.35)] ring-4 ring-[var(--vp-accent)]/30 hover:ring-[var(--vp-accent)]/60 animate-pulse-glow"
                data-testid="spotlight-plan-cta"
              >
                <Sparkles className="w-5 h-5 text-[var(--vp-accent)]" />
                Plan My Trip to {data.name}
              </button>
              <p className="text-[11px] text-[var(--vp-muted)] mt-3 tracking-wide">
                Free AI concierge · Instantly tailored · Verified by human travel experts
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FactCard({ icon, label, primary, secondary }) {
  return (
    <div className="bg-[var(--vp-bg)] border border-[var(--vp-border)] rounded-xl p-4">
      <div className="flex items-center gap-2 text-[var(--vp-secondary)] mb-2">
        {icon}
        <span className="vp-label">{label}</span>
      </div>
      <div className="text-[15px] text-[var(--vp-text)] font-semibold leading-snug">
        {primary}
      </div>
      <div className="text-xs text-[var(--vp-muted)] mt-1">{secondary}</div>
    </div>
  );
}

// Top-3-then-Show-More wrapper around AttractionRow. Renders the first 3 attractions
// instantly; toggling "+ Show More Spots" reveals items 4-6 with a smooth opacity/height
// transition (no layout shift) and the button label flips to "Show Less".
function AttractionsList({ items, destination }) {
  const [expanded, setExpanded] = useState(false);
  const first = items.slice(0, 3);
  const rest = items.slice(3, 6);
  const hasMore = rest.length > 0;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-serif-vp text-2xl md:text-3xl text-[var(--vp-primary)]">
          Top sightseeing in {destination}
        </h2>
        <span className="vp-label text-[var(--vp-muted)]">CLICK TO EXPLORE</span>
      </div>
      <div className="space-y-2.5" data-testid="spotlight-attractions">
        {first.map((a, i) => (
          <AttractionRow key={`${destination}-${i}`} idx={i} name={a.name} blurb={a.blurb} destination={destination} />
        ))}
        {hasMore && (
          <div
            className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-in-out ${
              expanded ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"
            }`}
            aria-hidden={!expanded}
          >
            <div className="space-y-2.5 pt-2.5">
              {rest.map((a, i) => (
                <AttractionRow
                  key={`${destination}-extra-${i}`}
                  idx={i + 3}
                  name={a.name}
                  blurb={a.blurb}
                  destination={destination}
                />
              ))}
            </div>
          </div>
        )}
        {hasMore && (
          <div className="pt-2 flex justify-center">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--vp-secondary)] hover:text-[var(--vp-primary)] tracking-wide group"
              data-testid="spotlight-show-more"
              aria-expanded={expanded}
            >
              <span className="border-b border-dashed border-current">
                {expanded ? "Show Less" : `+ Show More Spots (${rest.length})`}
              </span>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Expandable text-only sightseeing row. Click to toggle the description and reveal
// "View on Google Maps" + "Read more on Wikipedia" deep-links. No images = no risk
// of mismatched photos.
function AttractionRow({ idx, name, blurb, destination }) {
  const [open, setOpen] = useState(false);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name} ${destination}`)}`;
  const wikiUrl = `https://en.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(`${name} ${destination}`)}`;
  return (
    <div
      className="bg-white border border-[var(--vp-border)] rounded-xl overflow-hidden hover:border-[var(--vp-primary)]/40 transition-colors"
      data-testid={`spotlight-attraction-${idx}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left group"
        aria-expanded={open}
        data-testid={`spotlight-attraction-toggle-${idx}`}
      >
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[var(--vp-accent)]/15 text-[var(--vp-secondary)] flex items-center justify-center text-xs font-bold">
          {idx + 1}
        </span>
        <span className="flex-1 font-serif-vp text-lg text-[var(--vp-primary)] leading-snug">
          {name}
        </span>
        {/* Interactive visual cue — pin + arrow that nudges right on hover */}
        <span className="flex items-center gap-1 text-[var(--vp-secondary)] text-xs font-semibold tracking-wider opacity-70 group-hover:opacity-100 transition-opacity">
          <MapPin className="w-3.5 h-3.5" />
          <ChevronDown
            className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-[var(--vp-border)] animate-fade-up">
          <p className="text-sm text-[var(--vp-text)] leading-relaxed">{blurb}</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--vp-primary)] bg-[var(--vp-bg)] hover:bg-[var(--vp-accent)]/20 border border-[var(--vp-border)] px-3 py-1.5 rounded-full transition-colors"
              data-testid={`spotlight-attraction-maps-${idx}`}
            >
              <MapPin className="w-3 h-3" /> View on Google Maps
            </a>
            <a
              href={wikiUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--vp-primary)] bg-[var(--vp-bg)] hover:bg-[var(--vp-accent)]/20 border border-[var(--vp-border)] px-3 py-1.5 rounded-full transition-colors"
              data-testid={`spotlight-attraction-wiki-${idx}`}
            >
              <Compass className="w-3 h-3" /> Read more on Wikipedia
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
